import React, { useState } from 'react';
import DecisionCard from './components/DecisionCard';
import ChatMessage from './components/ChatMessage';

function App() {
  const [decision, setDecision] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [currentResponse, setCurrentResponse] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!decision.trim()) return;

    const userMessage = { type: 'user', content: decision };
    setConversation(prev => [...prev, userMessage]);
    setLoading(true);
    setCurrentResponse(null);

    try {
      const response = await fetch('/api/decide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ decision: decision.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to get decision');
      }

      const data = await response.json();
      setCurrentResponse(data);
      
      const aiMessage = { 
        type: 'ai', 
        content: `I've analyzed your decision: "${decision}"`,
        data: data 
      };
      setConversation(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = { 
        type: 'error', 
        content: 'Sorry, I encountered an error while processing your decision. Please try again.' 
      };
      setConversation(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setDecision('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto p-6">
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-4">
            AI Life-Ops
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Your intelligent decision assistant powered by advanced AI. 
            Get personalized recommendations for life's toughest choices.
          </p>
        </header>

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 mb-8">
          <form onSubmit={handleSubmit} className="flex gap-4">
            <input
              type="text"
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              placeholder="What decision do you need help with? (e.g., 'What should I cook today?' or 'Should I buy this laptop?')"
              className="flex-1 px-6 py-4 text-lg border-0 rounded-2xl bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 shadow-sm"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !decision.trim()}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold text-lg"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Analyzing...</span>
                </div>
              ) : (
                'Get Decision'
              )}
            </button>
          </form>
        </div>

        <div className="space-y-8">
          {conversation.map((message, index) => (
            <ChatMessage key={index} message={message} />
          ))}
          
          {loading && (
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-4 text-lg text-gray-600 font-medium">Analyzing your decision with AI...</span>
              </div>
            </div>
          )}

          {currentResponse && (
            <DecisionCard response={currentResponse} />
          )}
        </div>
      </div>
    </div>
  );
}

export default App; 